// Resolves playoff placeholders ("Pool A position 3", "Gagnant de la 24", "1ère position")
// into actual team names once preliminary scores are 'final'.
//
// Standings computation follows the tournament rules (Règlements → "Départage d'égalité au classement"):
//   1. Plus grand nombre de points
//   2. Plus grand nombre de victoires
//   3. Moins de défaites
//   4. Moins de buts contre (brut)
//   5. Résultat face-à-face (uniquement si exactement 2 équipes à égalité et qu'elles se sont
//      affrontées en phase préliminaire)
//   6. Différentiel (buts pour − buts contre), plafonné à ±7 par partie
//   7. Plus de buts pour (brut)
//   8. Moins de minutes de pénalités pendant les préliminaires
//   9. Fallback déterministe (nom d'équipe) — à la place d'un tirage au sort
//
// Note: si 3 équipes ou plus sont à égalité et qu'elles ne se sont pas toutes affrontées,
// les étapes 5 (face-à-face) et 8 (PIM) sont ignorées pour ce groupe (règle des Règlements).
import { SCHEDULE } from "../data/tournament";

const DIFF_CAP = 7; // ±7 goals per game cap for differential

const SCHEDULE_BY_NUM = SCHEDULE.reduce((m, g) => {
  m[g.num] = g;
  return m;
}, {});

function resolveOne(label, category, scoreByGame, standingsByCat, _seen) {
  if (!label || typeof label !== "string") return { value: label, resolved: false };

  // "Pool A position N" / "Pool B position N"  (also "Pool A pos N")
  const posMatch = label.match(/position\s+(\d+)/i) || label.match(/pos\s+(\d+)/i);
  if (posMatch) {
    const pos = parseInt(posMatch[1], 10);
    const stand = standingsByCat?.[category];
    if (stand && stand[pos - 1] && stand[pos - 1].team) {
      return { value: stand[pos - 1].team, resolved: true };
    }
    return { value: label, resolved: false };
  }

  // "1ère position" / "1ere position" / "Première position"
  if (/(1[èe]re|premi[èe]re)\s+position/i.test(label)) {
    const stand = standingsByCat?.[category];
    if (stand && stand[0] && stand[0].team) return { value: stand[0].team, resolved: true };
    return { value: label, resolved: false };
  }

  // "Gagnant de la N"
  const winnerMatch = label.match(/gagnant\s+de\s+la\s+(\d+)/i);
  if (winnerMatch) {
    const num = parseInt(winnerMatch[1], 10);
    if (_seen.has(num)) return { value: label, resolved: false };
    _seen.add(num);

    const game = SCHEDULE_BY_NUM[num];
    const sc = scoreByGame?.[num];
    if (!game || !sc || sc.status !== "final" || sc.away_score == null || sc.home_score == null) {
      return { value: label, resolved: false };
    }
    const innerAway = resolveOne(game.away, game.category, scoreByGame, standingsByCat, _seen);
    const innerHome = resolveOne(game.home, game.category, scoreByGame, standingsByCat, _seen);

    let winner;
    if (sc.away_score > sc.home_score) winner = innerAway.value;
    else if (sc.home_score > sc.away_score) winner = innerHome.value;
    else return { value: label, resolved: false };

    return { value: winner || label, resolved: !!winner };
  }

  return { value: label, resolved: true };
}

export function resolveGameTeams(game, scoreByGame, standingsByCat) {
  const away = resolveOne(game.away, game.category, scoreByGame, standingsByCat, new Set([game.num]));
  const home = resolveOne(game.home, game.category, scoreByGame, standingsByCat, new Set([game.num]));
  return {
    away: away.value,
    home: home.value,
    awayResolved: away.resolved,
    homeResolved: home.resolved,
  };
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Head-to-head result between two team names over preliminary games.
// Returns: 1 if A beat B overall, -1 if B beat A, 0 if tie/no matchup.
function headToHead(teamA, teamB, scoreByGame) {
  let aWins = 0;
  let bWins = 0;
  let played = 0;
  SCHEDULE.forEach((g) => {
    if (g.phase) return;
    const s = scoreByGame?.[g.num];
    if (!s || s.status !== "final" || s.away_score == null || s.home_score == null) return;
    const teams = [g.away, g.home];
    if (!teams.includes(teamA) || !teams.includes(teamB)) return;
    played++;
    const aIsAway = g.away === teamA;
    const aScore = aIsAway ? s.away_score : s.home_score;
    const bScore = aIsAway ? s.home_score : s.away_score;
    if (aScore > bScore) aWins++;
    else if (bScore > aScore) bWins++;
  });
  if (played === 0) return 0;
  if (aWins > bWins) return 1;
  if (bWins > aWins) return -1;
  return 0;
}

// Did every pair among `teams` play each other at least once in preliminaries?
function allTeamsFaced(teams, scoreByGame) {
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      let found = false;
      for (const g of SCHEDULE) {
        if (g.phase) continue;
        const s = scoreByGame?.[g.num];
        if (!s || s.status !== "final") continue;
        if ((g.away === teams[i] && g.home === teams[j]) ||
            (g.away === teams[j] && g.home === teams[i])) {
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
  }
  return true;
}

// Compare two team stat rows using the full tiebreaker chain (except head-to-head + PIM,
// which are handled at the group level in computeStandings).
// Returns negative if a ranks higher, positive if b ranks higher.
function compareByBasicTiebreakers(a, b) {
  if (b.pts !== a.pts) return b.pts - a.pts;
  if (b.v !== a.v) return b.v - a.v;            // more wins
  if (a.d !== b.d) return a.d - b.d;            // fewer losses
  if (a.ga !== b.ga) return a.ga - b.ga;        // fewer goals against
  return 0;
}

function compareAfterHeadToHead(a, b) {
  if (b.diffCapped !== a.diffCapped) return b.diffCapped - a.diffCapped; // higher capped diff
  if (b.gf !== a.gf) return b.gf - a.gf;                                  // more goals for
  if (a.pim !== b.pim) return a.pim - b.pim;                              // fewer PIM
  return a.team.localeCompare(b.team);                                    // deterministic fallback
}

// Compute standings (preliminary games only) for a given list of teams.
// Options:
//   pimByTeam: { [teamName]: total_pim_in_prelims } — optional
export function computeStandings(scoreByGame, teams, pimByTeam = {}) {
  const stats = {};
  teams.forEach((t) => {
    stats[t] = {
      team: t,
      pj: 0, v: 0, d: 0, n: 0, pts: 0,
      gf: 0, ga: 0,
      diffCapped: 0,
      pim: pimByTeam[t] || 0,
    };
  });
  SCHEDULE.forEach((g) => {
    if (g.phase) return;
    if (!stats[g.away] || !stats[g.home]) return;
    const s = scoreByGame?.[g.num];
    if (!s || s.status !== "final" || s.away_score == null || s.home_score == null) return;
    const aw = stats[g.away];
    const hm = stats[g.home];
    aw.pj++; hm.pj++;
    aw.gf += s.away_score; aw.ga += s.home_score;
    hm.gf += s.home_score; hm.ga += s.away_score;
    aw.diffCapped += clamp(s.away_score - s.home_score, -DIFF_CAP, DIFF_CAP);
    hm.diffCapped += clamp(s.home_score - s.away_score, -DIFF_CAP, DIFF_CAP);
    if (s.away_score > s.home_score) {
      aw.v++; aw.pts += 2; hm.d++;
    } else if (s.home_score > s.away_score) {
      hm.v++; hm.pts += 2; aw.d++;
    } else {
      aw.n++; hm.n++; aw.pts++; hm.pts++;
    }
  });

  const rows = Object.values(stats);

  // First pass — sort by basic tiebreakers 1-4 (pts, V, D, GA).
  rows.sort(compareByBasicTiebreakers);

  // Group rows that are equal after basic tiebreakers to resolve head-to-head (5),
  // DIFF capped (6), GF (7), PIM (8), then deterministic fallback.
  const result = [];
  let i = 0;
  while (i < rows.length) {
    let j = i + 1;
    while (j < rows.length && compareByBasicTiebreakers(rows[i], rows[j]) === 0) j++;
    const group = rows.slice(i, j);

    if (group.length === 1) {
      result.push(group[0]);
    } else if (group.length === 2) {
      // Exactly 2 teams tied → use head-to-head if they played
      const [a, b] = group;
      const h2h = headToHead(a.team, b.team, scoreByGame);
      if (h2h !== 0) {
        group.sort((x, y) => {
          if (x.team === a.team && y.team === b.team) return -h2h;
          if (x.team === b.team && y.team === a.team) return h2h;
          return 0;
        });
      } else {
        group.sort(compareAfterHeadToHead);
      }
      result.push(...group);
    } else {
      // 3+ teams tied: apply head-to-head only if all teams faced each other; otherwise skip.
      const applyH2H = allTeamsFaced(group.map((r) => r.team), scoreByGame);
      if (applyH2H) {
        // Mini-league of head-to-head points among tied teams (wins count +2, tie +1).
        const miniPts = {};
        group.forEach((r) => { miniPts[r.team] = 0; });
        for (let a = 0; a < group.length; a++) {
          for (let b = a + 1; b < group.length; b++) {
            const h = headToHead(group[a].team, group[b].team, scoreByGame);
            if (h > 0) miniPts[group[a].team] += 2;
            else if (h < 0) miniPts[group[b].team] += 2;
            else { miniPts[group[a].team]++; miniPts[group[b].team]++; }
          }
        }
        group.sort((x, y) => {
          if (miniPts[y.team] !== miniPts[x.team]) return miniPts[y.team] - miniPts[x.team];
          return compareAfterHeadToHead(x, y);
        });
      } else {
        // Note: per règlement, PIM (étape 8) aussi exclu quand 3+ équipes ne se sont pas toutes
        // affrontées. On saute directement DIFF → GF → fallback.
        group.sort((x, y) => {
          if (y.diffCapped !== x.diffCapped) return y.diffCapped - x.diffCapped;
          if (y.gf !== x.gf) return y.gf - x.gf;
          return x.team.localeCompare(y.team);
        });
      }
      result.push(...group);
    }
    i = j;
  }
  return result;
}
